import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Dog, Cat } from "lucide-react";
import { Loading } from "../components/Layout";
import { api, errMsg } from "../lib/api";
import { formatDKK, SPECIES, PRICE_TYPE } from "../lib/labels";

const StatCard = ({ sp, st, slug }) => {
  const Icon = sp === "dog" ? Dog : Cat;
  return (
    <div className="card-vp p-6" data-testid={`price-stats-${sp}`}>
      <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-[#087F72]" /><h2 className="font-heading font-bold text-[#153C3A]">{SPECIES[sp]}</h2></div>
      {st.fixed_count ? (
        <dl className="grid grid-cols-3 gap-3 mt-4 text-center">
          {[["Laveste", st.min], ["Median", st.median], ["Højeste", st.max]].map(([l, v]) => (
            <div key={l} className="rounded-lg bg-[#F7FAF9] p-3"><dt className="text-xs text-[#52706D]">{l}</dt><dd className="font-heading font-bold text-[#153C3A] mt-1">{formatDKK(v)}</dd></div>
          ))}
        </dl>
      ) : <p className="text-sm text-[#52706D] mt-3">Ingen faste priser endnu.</p>}
      <p className="text-xs text-[#52706D] mt-3">{st.count} priser fra {st.clinics} klinikker{st.fixed_count ? ` · beregnet ud fra ${st.fixed_count} faste priser` : ""}</p>
      <Link to={`/sammenlign?species=${sp}&service=${slug}&radius=500`} className="btn-primary w-full mt-4" data-testid={`compare-${sp}-button`}>Sammenlign priser for {SPECIES[sp].toLowerCase()}</Link>
    </div>
  );
};

const money = (p) => `${p.price_type === "FROM" ? "fra " : ""}${formatDKK(p.amount)}${p.amount_max ? ` – ${formatDKK(p.amount_max)}` : ""}`;

const PriceList = ({ prices }) => (
  <section className="mt-10" data-testid="treatment-prices">
    <h2 className="text-xl font-bold text-[#153C3A]">Publicerede priser fra klinikker</h2>
    {prices.length === 0 ? <p className="text-sm text-[#52706D] mt-2">Vi har endnu ingen verificerede priser på denne behandling.</p> : (
      <div className="overflow-x-auto mt-3"><table className="table-vp text-sm">
        <thead><tr><th>Klinik</th><th>Dyr</th><th>Pris</th><th>Type</th><th>Kontrolleret</th></tr></thead>
        <tbody>{prices.slice(0, 40).map((p, i) => (
          <tr key={i}><td><Link to={`/dyreklinikker/${p.clinic_slug}`} className="text-[#087F72] hover:underline">{p.clinic_name}</Link></td><td>{SPECIES[p.species_code]}</td>
            <td className="whitespace-nowrap font-semibold">{money(p)}</td><td>{PRICE_TYPE[p.price_type] || p.price_type}</td><td className="text-xs">{p.last_verified_at ? new Date(p.last_verified_at).toLocaleDateString("da-DK") : "–"}</td></tr>))}</tbody>
      </table></div>)}
  </section>
);

const Extras = ({ x }) => (
  <div className="grid md:grid-cols-3 gap-8 mt-10 text-sm">
    <div><h2 className="font-bold text-[#153C3A]">Hvad påvirker prisen?</h2><ul className="mt-2 space-y-2 text-[#52706D] list-disc pl-5" data-testid="treatment-factors">{x.factors.map((f) => <li key={f}>{f}</li>)}</ul></div>
    <div><h2 className="font-bold text-[#153C3A]">Relaterede behandlinger</h2><ul className="mt-2 space-y-1" data-testid="treatment-related">{x.related.map((r) => <li key={r.slug}><Link to={`/behandlinger/${r.slug}`} className="text-[#087F72] hover:underline">{r.name_da}</Link></li>)}</ul></div>
    <div><h2 className="font-bold text-[#153C3A]">Find dyrlæge i din by</h2><ul className="mt-2 grid grid-cols-2 gap-1" data-testid="treatment-cities">{x.cities.map((c) => <li key={c.slug}><Link to={`/dyrlaege/${c.slug}`} className="text-[#087F72] hover:underline">{c.name}</Link></li>)}</ul></div>
  </div>
);

export default function TreatmentDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [x, setX] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setS(null); setX(null);
    api.get(`/public/services/${slug}`).then((r) => setS(r.data)).catch((e) => setError(errMsg(e)));
    api.get(`/seo/treatment/${slug}`).then((r) => setX(r.data)).catch(() => {});
  }, [slug]);
  if (error) return <div className="container-vp py-16 text-[#52706D]" data-testid="treatment-error">{error}</div>;
  if (!s) return <Loading />;
  const sp = s.species_codes.map((c) => SPECIES[c].toLowerCase()).join(" og ");
  return (
    <div className="container-vp py-10" data-testid="treatment-detail-page">
      <p className="eyebrow">{s.category?.name_da}</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold text-[#153C3A] mt-2" data-testid="treatment-title">{s.name_da}: priser for {sp}</h1>
      <p className="text-base md:text-lg text-[#52706D] mt-4 max-w-2xl">{s.description_da} {s.typical_conditions}</p>
      <div className="grid lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 content-start">
          {s.species_codes.map((c) => <StatCard key={c} sp={c} st={s.price_stats[c]} slug={s.slug} />)}
        </div>
        <aside className="card-vp p-6 space-y-5">
          {s.variants?.length > 0 && (
            <div><h2 className="text-sm font-bold text-[#153C3A]">Varianter og vægtklasser</h2>
              <ul className="mt-2 space-y-1 text-sm text-[#52706D]">{s.variants.map((v) => <li key={v.code}>• {v.name_da}</li>)}</ul></div>
          )}
          {s.aliases?.length > 0 && <div><h2 className="text-sm font-bold text-[#153C3A]">Kaldes også</h2><p className="text-sm text-[#52706D] mt-1">{s.aliases.join(", ")}</p></div>}
          <p className="text-xs text-[#52706D]">Medianen beregnes kun ud fra faste priser. Fra-priser og intervaller indgår ikke. VetPris giver ikke medicinsk rådgivning – tal med din dyrlæge om behandlingen.</p>
          {x?.updated && <p className="text-xs text-[#52706D]" data-testid="treatment-updated">Senest opdateret: {new Date(x.updated).toLocaleDateString("da-DK")}</p>}
        </aside>
      </div>
      {x && <PriceList prices={x.prices} />}
      {x && <Extras x={x} />}
    </div>
  );
}
